<template>
  <BContainer :title="$t('title.reportQuestionsAnswers')">
    <ReportFilter @apply="getQuestionAndAnswers">
      <template v-slot:customButtons>
        <DownloadExcel
          v-if="isExportButtonActive"
          :data="excelData"
          :fields="excelFields"
          :name="`${title}.xls`"
        >
          <button class="button is-link">{{ $t('button.export') }}</button>
        </DownloadExcel>
        <DownloadActionTrackingCalendarButton :answerSetId="answerSetId" v-if="answerSetId && isExportButtonActive" hide-label style="margin-left: 10px;;"/>
      </template>
    </ReportFilter>
    <section class="section b-portlet has-background-light" v-if="tableData">
      <div v-if="isGroup">
        <article
          class="message is-light"
          v-for="answerSet in tableData.answerSets"
          :key="answerSet.id"
        >
          <div class="message-header ">
            <div class="title is-size-5">
              {{ answerSet.process.name }}
            </div>
          </div>
          <div class="message-body">
            <b-table
              :data="answerSet.answers"
              :bordered="true"
              :striped="true"
              :hoverable="true"
              :mobile-cards="true"
            >
              <template slot-scope="props">
                <b-table-column field="no" label="No" width="40" numeric>{{
                  props.row.question.no
                }}</b-table-column>
                <b-table-column field="name" :label="$t('title.question')">
                  {{ props.row.question.name }}
                  <p v-if="props.row.description" class="is-italic has-text-weight-bold">
                    {{ props.row.description }}
                  </p>
                </b-table-column>
                <b-table-column
                  field="answer"
                  width="40"
                  :label="$t('validation.attributes.answer')"
                >
                  <b-button :size="`is-small ${getAnswerClassForPoint(props.row.answerPoint)}`">{{
                    props.row.answerPoint
                  }}</b-button>
                </b-table-column>
                <b-table-column
                  field="photos"
                  :label="$t('validation.attributes.photos')"
                  width="160"
                >
                  <div class="photo-report-thumbs">
                    <button
                      v-for="(url, index) in props.row.photos"
                      :key="url"
                      type="button"
                      class="photo-report-thumb"
                      :aria-label="$t('button.enlargePhoto')"
                      @click="openPhotoPreview(url)"
                    >
                      <img :src="url" :alt="`${$t('title.photo')} ${index + 1}`" />
                    </button>
                  </div>
                </b-table-column>
              </template>
            </b-table>
          </div>
        </article>
      </div>
      <b-table
        v-else-if="tableData.length > 0"
        :data="tableData"
        :bordered="true"
        :striped="true"
        :hoverable="true"
        :mobile-cards="true"
      >
        <template slot-scope="props">
          <b-table-column field="no" label="No" width="40" numeric>{{
            props.row.no
          }}</b-table-column>
          <b-table-column field="name" :label="$t('title.question')">
            {{ props.row.name }}
            <p v-if="props.row.description" :class="`is-italic ${props.row.hasActionTracking?'has-text-weight-bold':''}`" v-html="props.row.description" />
          </b-table-column>
          <b-table-column field="answer" :label="$t('validation.attributes.answer')">
            <b-tag type="is-success" v-if="props.row.answer === true">{{ $t('button.yes') }}</b-tag>
            <b-tag type="is-danger" v-if="props.row.answer === false">{{ $t('button.no') }}</b-tag>
          </b-table-column>
          <b-table-column
            field="photos"
            :label="$t('validation.attributes.photos')"
            width="160"
          >
            <div class="photo-report-thumbs">
              <button
                v-for="(url, index) in props.row.photos"
                :key="url"
                type="button"
                class="photo-report-thumb"
                :aria-label="$t('button.enlargePhoto')"
                @click="openPhotoPreview(url)"
              >
                <img :src="url" :alt="`${$t('title.photo')} ${index + 1}`" />
              </button>
            </div>
          </b-table-column>
        </template>
      </b-table>
    </section>
  </BContainer>
</template>
<script>
import ApiService from '../../_api/api.service'
import ReportFilter from './ReportFilter.vue'
import DownloadActionTrackingCalendarButton from '@/components/DownloadActionTrackingCalendarButton.vue'
import PhotoPreview from '@/components/PhotoPreview.vue'
import DateFilter from '@/_helpers/date.filter'

// The api allows this many photos per answer
const MAX_PHOTOS = 3

const toPhotos = photos => (Array.isArray(photos) ? photos : [])

const escapeHtml = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

// vue-json-excel cannot embed images, but it writes the file as an html table and puts cell
// values in as they are, so each photo becomes a link. A cell keeps a single link in Excel,
// hence a column per photo
const toPhotoLinks = (photos, label) =>
  toPhotos(photos)
    .slice(0, MAX_PHOTOS)
    .reduce((links, url, index) => {
      const text = escapeHtml(label(index + 1))
      links[`photo${index + 1}`] = `<a href="${escapeHtml(url)}">${text}</a>`
      return links
    }, {})

export default {
  components: { ReportFilter, DownloadActionTrackingCalendarButton },
  data () {
    return {
      tableData: [],
      title: 'report-data',
      excelFields: {},
      excelData: [],
      isExportButtonActive: false,
      isGroup: false,
      answerSetId: null
    }
  },
  mounted () {
    this.excelFields = this.getExcelFields()
  },
  methods: {
    getQuestionAndAnswers (params) {
      const { filter, isGroup } = params
      this.title = filter.content.title
      this.score = null
      this.isGroup = isGroup
      this.answerSetId = filter.answerSetId

      if (isGroup) {
        ApiService.get(`/answerset/group/${filter.answerSetGroupId}/answers`).then(data => {
          this.isExportButtonActive = true
          data.answerSets.forEach(answerSet => {
            answerSet.answers.forEach(answer => {
              answer.photos = toPhotos(answer.photos)
            })
          })
          this.tableData = data
          this.excelData = []
          data.answerSets.forEach(answerSet => {
            this.excelData.push({})
            this.excelData.push({
              name: answerSet.process.name
            })
            answerSet.answers.forEach(answer => {
              this.excelData.push({
                no: answer.question.no,
                name: answer.question.name,
                description: answer.description,
                answer: answer.answerPoint,
                ...toPhotoLinks(answer.photos, this.photoLabel)
              })
            })
          })
        })
      } else {
        ApiService.get(`/answerset/${filter.answerSetId}/answers`).then(data => {
          this.isExportButtonActive = true

          this.tableData = data.map(d => {
            const hasActionTracking = d.actionTitle || d.actionAssignors || d.actionDeliveryDate
            const description = hasActionTracking ? `<br/><b>Aksiyon Tanımı:</b> ${d.actionTitle}<br/><b>Atanan Kişi:</b> ${d.actionAssignors}<br/><b>Aksiyon Teslim Tarihi:</b> ${DateFilter.date(d.actionDeliveryDate)}<br/><b>Açıklama:</b> ${d.description}` : `${d.description ? '<br/><b>Açıklama: </b>' + d.description : ''}`
            return {
              no: d.question.no,
              name: d.question.name,
              description,
              answer: d.answer,
              photos: toPhotos(d.photos)
            }
          }
          )
          this.excelData = data.map(d => {
            const hasActionTracking = d.actionTitle || d.actionAssignors || d.actionDeliveryDate
            const description = hasActionTracking ? `Aksiyon Tanımı: ${d.actionTitle}\nAtanan Kişi: ${d.actionAssignors}\nAksiyon Teslim Tarihi: ${DateFilter.date(d.actionDeliveryDate)}\nAçıklama: ${d.description}` : d.description
            return {
              no: d.question.no,
              name: d.question.name,
              description,
              answer: d.answer === true ? this.$t('button.yes') : this.$t('button.no'),
              ...toPhotoLinks(d.photos, this.photoLabel)
            }
          })
        })
      }
    },
    getExcelFields () {
      const fields = {}
      fields[this.$t('validation.attributes.no')] = 'no'
      fields[this.$t('validation.attributes.question')] = 'name'
      fields[this.$t('validation.attributes.description')] = 'description'
      fields[this.$t('validation.attributes.answer')] = 'answer'
      for (let number = 1; number <= MAX_PHOTOS; number++) {
        fields[this.photoLabel(number)] = `photo${number}`
      }
      return fields
    },
    photoLabel (number) {
      return `${this.$t('title.photo')} ${number}`
    },
    openPhotoPreview (url) {
      this.$buefy.modal.open({
        parent: this,
        component: PhotoPreview,
        props: { url }
      })
    },
    getAnswerClassForPoint (point) {
      switch (point) {
        case 0:
          return 'is-danger'
        case 1:
          return 'is-orange'
        case 2:
          return 'is-gray'
        case 3:
          return 'is-info'
        case 4:
          return 'is-success'
        default:
          return ''
      }
    }
  }
}
</script>

<style scoped>
/* The api allows 3 photos per answer, and the column is wide enough to keep them on one line
   so a row with photos grows no taller than a single thumbnail */
.photo-report-thumbs {
  display: flex;
}

.photo-report-thumb {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  margin-right: 4px;
  padding: 0;
  overflow: hidden;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background: none;
  cursor: zoom-in;
  transition: transform 0.15s;
}

.photo-report-thumb:last-child {
  margin-right: 0;
}

.photo-report-thumb:hover,
.photo-report-thumb:focus {
  transform: scale(1.1);
}

.photo-report-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
